import { getFormTabErrorCounts } from '../../../../../../lib/formTabErrors';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm, type FieldErrors } from 'react-hook-form';
import { BadgeDollarSign, Building2, Link2, ListTree, UserRound } from 'lucide-react';
import {
  DashboardPageFormActions,
  DashboardPageFormTab,
  DashboardPageFormTabList,
  DashboardPageFormTabPanel,
} from '@shigo-ui/dashboard-ui';
import { Button, Form } from '@shigo-ui/ui';

import { EditorSectionTabs } from '../../../../../../components/forms/EditorSectionTabs';
import { useEditorSectionControls } from '../../../../../../components/forms/useEditorSection';
import { dashboardT } from '../../../../../../lib/messages';
import { NetworkAvailableFeature } from '../../../../../auth/networkFeatures';
import { useNetworkFeatures } from '../../../../../auth/useNetworkFeatures';
import { createPublisherFormSchema, type PublisherFormValue } from '../../../publisherFormSchema';
import { PublisherCompanyFields } from '../PublisherCompanyFields';
import { PublisherGeneralFields } from '../PublisherGeneralFields';
import { PublisherPricingFields } from '../PublisherPricingFields';
import { PublisherSchainFields } from '../PublisherSchainFields';
import { PublisherXpMappingsFields } from '../PublisherXpMappingsFields';

enum PublisherEditorTab {
  Publisher = 'publisher',
  Company = 'company',
  Schain = 'schain',
  Pricing = 'pricing',
  XpMappings = 'xp-mappings',
}

export function PublisherEditorForm({
  initialValue,
  editing,
  saving,
  onCancel,
  onSubmit,
}: {
  initialValue: PublisherFormValue;
  editing: boolean;
  saving: boolean;
  onCancel: () => void;
  onSubmit: (value: PublisherFormValue) => Promise<void>;
}) {
  'use no memo';
  const [editorSection, setActiveTab] = useEditorSectionControls(
    PublisherEditorTab.Publisher,
    Object.values(PublisherEditorTab),
  );
  const { isFeatureAllowed } = useNetworkFeatures();
  const campaignFeatureAllowed = isFeatureAllowed(NetworkAvailableFeature.Campaign);
  const form = useForm<PublisherFormValue>({
    defaultValues: initialValue,
    resolver: zodResolver(createPublisherFormSchema({ campaignFeatureAllowed })),
    mode: 'onSubmit',
  });
  function invalidSubmit(errors: FieldErrors<PublisherFormValue>) {
    if (hasCompanyErrors(errors)) setActiveTab(PublisherEditorTab.Company);
    else if (hasSchainErrors(errors)) setActiveTab(PublisherEditorTab.Schain);
    else if (hasPricingErrors(errors)) setActiveTab(PublisherEditorTab.Pricing);
    else if (errors.xpParametersMappings) setActiveTab(PublisherEditorTab.XpMappings);
    else setActiveTab(PublisherEditorTab.Publisher);
  }

  const tabErrors = getFormTabErrorCounts(
    form.formState.errors,
    {
      [PublisherEditorTab.Company]: [
        'companyName',
        'contactName',
        'email',
        'websiteUrl',
        'gamAdUnitId',
        'gamIdEnabled',
        'mcmActive',
        'phone',
        'address',
        'city',
        'state',
        'zipCode',
        'country',
      ],
      [PublisherEditorTab.Schain]: [
        'schainAsi',
        'schainSid',
        'rewritePublisherId',
        'alternativePublisherId',
      ],
      [PublisherEditorTab.Pricing]: [
        'revenueCostType',
        'revenueShare',
        'revenueCpmValue',
        'revenueShareIo',
        'saasCostType',
        'saasRevenueShare',
        'saasCpmMetric',
        'saasCpmValue',
        'whitelistAppsEnabled',
        'whitelistDomainsEnabled',
      ],
      [PublisherEditorTab.XpMappings]: ['xpParametersMappings'],
    },
    PublisherEditorTab.Publisher,
  );

  return (
    <Form {...form}>
      <form noValidate onSubmit={(event) => void form.handleSubmit(onSubmit, invalidSubmit)(event)}>
        <EditorSectionTabs controls={editorSection}>
          <DashboardPageFormTabList>
            <DashboardPageFormTab
              value={PublisherEditorTab.Publisher}
              errorCount={tabErrors[PublisherEditorTab.Publisher]}
              errorLabel={(count) => dashboardT('common.validationErrors', { count })}
              icon={<UserRound className="h-4 w-4" aria-hidden="true" />}
            >
              {dashboardT('publishers.tabs.publisher')}
            </DashboardPageFormTab>
            <DashboardPageFormTab
              value={PublisherEditorTab.Company}
              errorCount={tabErrors[PublisherEditorTab.Company]}
              errorLabel={(count) => dashboardT('common.validationErrors', { count })}
              icon={<Building2 className="h-4 w-4" aria-hidden="true" />}
            >
              {dashboardT('publishers.tabs.company')}
            </DashboardPageFormTab>
            <DashboardPageFormTab
              value={PublisherEditorTab.Schain}
              errorCount={tabErrors[PublisherEditorTab.Schain]}
              errorLabel={(count) => dashboardT('common.validationErrors', { count })}
              icon={<Link2 className="h-4 w-4" aria-hidden="true" />}
            >
              {dashboardT('publishers.tabs.schain')}
            </DashboardPageFormTab>
            <DashboardPageFormTab
              value={PublisherEditorTab.Pricing}
              errorCount={tabErrors[PublisherEditorTab.Pricing]}
              errorLabel={(count) => dashboardT('common.validationErrors', { count })}
              icon={<BadgeDollarSign className="h-4 w-4" aria-hidden="true" />}
            >
              {dashboardT('publishers.tabs.pricing')}
            </DashboardPageFormTab>
            <DashboardPageFormTab
              value={PublisherEditorTab.XpMappings}
              errorCount={tabErrors[PublisherEditorTab.XpMappings]}
              errorLabel={(count) => dashboardT('common.validationErrors', { count })}
              icon={<ListTree className="h-4 w-4" aria-hidden="true" />}
            >
              {dashboardT('publishers.tabs.xp')}
            </DashboardPageFormTab>
          </DashboardPageFormTabList>
          <DashboardPageFormTabPanel value={PublisherEditorTab.Publisher}>
            <PublisherGeneralFields editing={editing} />
          </DashboardPageFormTabPanel>
          <DashboardPageFormTabPanel value={PublisherEditorTab.Company}>
            <PublisherCompanyFields campaignFeatureAllowed={campaignFeatureAllowed} />
          </DashboardPageFormTabPanel>
          <DashboardPageFormTabPanel value={PublisherEditorTab.Schain}>
            <PublisherSchainFields />
          </DashboardPageFormTabPanel>
          <DashboardPageFormTabPanel value={PublisherEditorTab.Pricing}>
            <PublisherPricingFields />
          </DashboardPageFormTabPanel>
          <DashboardPageFormTabPanel value={PublisherEditorTab.XpMappings}>
            <PublisherXpMappingsFields />
          </DashboardPageFormTabPanel>
        </EditorSectionTabs>
        {form.formState.errors.root?.server?.message ? (
          <p role="alert" className="text-destructive px-5 pb-3 text-sm">
            {form.formState.errors.root.server.message}
          </p>
        ) : null}
        <DashboardPageFormActions>
          <Button type="button" variant="outline" disabled={saving} onClick={onCancel}>
            {dashboardT('publishers.cancel')}
          </Button>
          <Button type="submit" disabled={saving}>
            {dashboardT(saving ? 'publishers.saving' : 'publishers.save')}
          </Button>
        </DashboardPageFormActions>
      </form>
    </Form>
  );
}

function hasCompanyErrors(errors: FieldErrors<PublisherFormValue>) {
  return [
    errors.companyName,
    errors.contactName,
    errors.email,
    errors.websiteUrl,
    errors.gamAdUnitId,
  ].some(Boolean);
}

function hasSchainErrors(errors: FieldErrors<PublisherFormValue>) {
  return Boolean(errors.alternativePublisherId);
}

function hasPricingErrors(errors: FieldErrors<PublisherFormValue>) {
  return [
    errors.revenueShare,
    errors.revenueCpmValue,
    errors.revenueShareIo,
    errors.saasRevenueShare,
    errors.saasCpmMetric,
    errors.saasCpmValue,
  ].some(Boolean);
}
